import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';

// Define the shape of the context data
interface AuthContextType {
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (newToken: string) => void;
    logout: () => void;
}

// Create the context with a default value (or undefined and check for it)
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// IMPORTANT: Do NOT end REACT_APP_API_URL with /api. It should be e.g. http://localhost:8000 or http://your-server.com
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const apiClient = axios.create({
    baseURL: API_URL,
});

// Add interceptor to include token in headers
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});


// Provider component
interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
    const [isLoading, setIsLoading] = useState<boolean>(true); // Start as true until initial check is done

    // Check token validity on initial load (optional but recommended)
    useEffect(() => {
        const checkToken = async () => {
            if (token) {
                try {
                    console.log("Token seems valid (initial check placeholder).");
                } catch (error) {
                    console.error("Token validation failed:", error);
                    logout();
                }
            }
            setIsLoading(false);
        };

        checkToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = useCallback((newToken: string) => {
        localStorage.setItem('authToken', newToken);
        setToken(newToken);
        // No need to manually set header here if using interceptor
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('authToken');
        setToken(null);
        // Optionally clear other user data
    }, []);

    // Add response interceptor to handle 401 errors globally
    useEffect(() => {
        const interceptor = apiClient.interceptors.response.use(
            response => response,
            error => {
                if (error.isAxiosError && error.response?.status === 401) {
                    console.log("Unauthorized request, logging out.");
                    logout();
                }
                return Promise.reject(error);
            }
        );

        // Cleanup interceptor on component unmount
        return () => {
            apiClient.interceptors.response.eject(interceptor);
        };
    }, [logout]);


    const value = {
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Optional: Export the configured Axios client if needed elsewhere
export { apiClient }; 